import { useCallback, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import {
  useDeactivateCaseAttachment,
  useGetPermittedFileExtensionsForCaseAttachments,
} from "@vega-api/generated/case-attachment-file-v1/case-attachment-file-v1";
import { useCreateCaseFromLpPortal } from "@vega-api/generated/case-management-v1/case-management-v1";
import { useLinkAttachmentToCase } from "@vega-api/generated/lp-portal-attachment-v1/lp-portal-attachment-v1";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { TextareaField } from "@vega-core/components/Form/Fields/TextareaField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { DEFAULT_UPLOAD_MIME_TYPES } from "@vega-core/constants/documents";
import { MAX_EMAIL_ATTACHMENTS } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { EmailAttachmentButton } from "@vega-modules/components/EmailAttachmentButton/EmailAttachmentButton";
import {
  type AttachmentItem,
  FileAttachmentItem,
} from "@vega-modules/components/FileAttachmentItem/FileAttachmentItem";

type SubmitTicketModalProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
};

type FormValues = {
  subject: string;
  description: string;
};

const validationSchema = Yup.object({
  subject: Yup.string()
    .trim()
    .required(m.support_submitTicket_subject_required()),
  description: Yup.string()
    .trim()
    .required(m.support_submitTicket_description_required()),
});

export const SubmitTicketModal = ({
  open,
  setOpen,
}: SubmitTicketModalProps) => {
  const showToast = useToast();
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const resetFormRef = useRef<(() => void) | null>(null);

  const { data: permittedFileTypesData } =
    useGetPermittedFileExtensionsForCaseAttachments();

  const { mutate: deactivateAttachment } = useDeactivateCaseAttachment();
  const { mutate: linkAttachment } = useLinkAttachmentToCase();

  const acceptString = (
    permittedFileTypesData?.data?.permittedFileTypes
      ? permittedFileTypesData.data.permittedFileTypes.map((type) => `.${type}`)
      : Object.keys(DEFAULT_UPLOAD_MIME_TYPES)
  ).join(",");

  const { mutate: createCase, isPending } = useCreateCaseFromLpPortal({
    mutation: {
      onSuccess: (response, variables) => {
        const caseId = response.data?.caseId;
        const attachmentIds = variables.data.attachments;

        if (caseId && attachmentIds && attachmentIds.length > 0) {
          attachmentIds.forEach((attachmentId) => {
            linkAttachment({ attachmentId, caseId });
          });
        }

        showToast({
          title: m.support_submitTicket_success(),
          variant: "success",
        });
        handleClose(resetFormRef.current ?? undefined);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error, {
          defaultTitle: m.support_submitTicket_error_title(),
          defaultMessage: m.support_submitTicket_error_description(),
        });
        showToast({
          title: errorMessage.title,
          description: errorMessage.description,
          variant: "destructive",
        });
      },
    },
  });

  const hasUploadingFiles = attachments.some((item) => item.isUploading);
  const hasFailedFiles = attachments.some((item) => item.isError);
  const canSubmit = !hasUploadingFiles && !hasFailedFiles;

  const handleClose = (resetForm?: () => void) => {
    attachments.forEach((item) => {
      if (item.attachment) {
        deactivateAttachment({ attachmentId: item.attachment.id });
      }
    });

    setAttachments([]);
    resetForm?.();
    setOpen(false);
  };

  const handleSubmit = (values: FormValues) => {
    const attachmentIds = attachments.reduce<string[]>((uploadedIds, item) => {
      if (isTruthy(item.attachment)) {
        uploadedIds.push(item.attachment.id);
      }

      return uploadedIds;
    }, []);

    createCase({
      data: {
        title: values.subject.trim(),
        description: values.description.trim(),
        attachments: attachmentIds.length > 0 ? attachmentIds : undefined,
      },
    });
  };

  const handleFileSelect = (files: File[]) => {
    const newAttachments = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
      isUploading: true,
      isError: false,
    }));
    setAttachments([...attachments, ...newAttachments]);
  };

  const handleUploadSuccess = useCallback(
    (
      itemId: string,
      attachment: { id: string; fileName: string; size: number },
    ) => {
      setAttachments((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? { ...item, attachment, isUploading: false, isError: false }
            : item,
        ),
      );
    },
    [],
  );

  const handleUploadStateChange = useCallback(
    (itemId: string, isUploading: boolean, isError: boolean) => {
      setAttachments((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, isUploading, isError } : item,
        ),
      );
    },
    [],
  );

  const handleRemoveAttachment = (itemId: string) => {
    setAttachments((prev) => prev.filter((item) => item.id !== itemId));
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{ subject: "", description: "" }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ resetForm, submitForm }) => {
        resetFormRef.current = resetForm;

        return (
          <ActionaryModal
            ModalButtonsGroupProps={{
              disabled: isPending || !canSubmit,
              disabledSecondary: isPending,
              mainAction: submitForm,
              mainActionLabel: m.btn_submit(),
              mainActionText: m.btn_submit(),
              secondaryAction: () => handleClose(resetForm),
              secondaryActionLabel: m.btn_cancel(),
              secondaryActionText: m.btn_cancel(),
            }}
            dataTestId="submit-ticket-modal"
            defaultWidth
            description={m.support_submitTicket_modal_subtitle()}
            fluidHeight
            open={open}
            setOpen={(value) => {
              if (!value) {
                handleClose(resetForm);
              } else {
                setOpen(value);
              }
            }}
            title={m.support_submitTicket_modal_title()}
          >
            <Form className="space-y-4 text-left">
              <InputField
                autoFocus
                label={m.support_submitTicket_subject_label()}
                name="subject"
                placeholder={m.support_submitTicket_subject_placeholder()}
                type="text"
              />

              <TextareaField
                isRequired
                label={m.support_submitTicket_description_label()}
                name="description"
                placeholder={m.support_submitTicket_description_placeholder()}
                rows={12}
              />

              {attachments.length > 0 && (
                <div className="space-y-2">
                  {attachments.map((item) => (
                    <FileAttachmentItem
                      item={item}
                      key={item.id}
                      onRemove={handleRemoveAttachment}
                      onUploadStateChange={handleUploadStateChange}
                      onUploadSuccess={handleUploadSuccess}
                    />
                  ))}
                </div>
              )}

              <EmailAttachmentButton
                accept={acceptString}
                disabled={attachments.length >= MAX_EMAIL_ATTACHMENTS}
                maxFiles={MAX_EMAIL_ATTACHMENTS - attachments.length}
                onFileSelect={handleFileSelect}
              />
            </Form>
          </ActionaryModal>
        );
      }}
    </Formik>
  );
};
